import { random } from 'lodash';
import { DEVIATIONS, METRIC_CODES, VALUES } from './data';

const getMetricValue = (aggregation, metricCode) => (aggregation
  ? VALUES[aggregation][metricCode]
  : VALUES.max[metricCode]);

export const powerRiseCalculate = ({
  start, end, current, aggregation,
}) => {
  let power;
  const delta = end - start;
  const metrics = {};

  METRIC_CODES.forEach((metricCode) => {
    const metricValue = getMetricValue(aggregation, metricCode);

    if (current >= end) {
      power = metricValue;
    } else {
      power = current * metricValue / delta;
    }

    metrics[metricCode] = power;
  });

  return metrics;
};

export const makeRiseBehavior = ({
  start, end, interval = 15000, aggregation,
}) => {
  const events = {};

  for (let i = 0; i < (end + interval - start); i += interval) {
    events[start + i] = {
      ts: start + i,
      metricValues: powerRiseCalculate({
        start,
        end,
        current: i,
        aggregation,
      }),
    };
  }

  return events;
};

export const powerStableCalculate = (aggregation) => {
  const metrics = {};

  METRIC_CODES.forEach((metricCode) => {
    const deviation = DEVIATIONS[metricCode];
    const rand = random(deviation, -deviation, true);

    const metricValue = getMetricValue(aggregation, metricCode);

    metrics[metricCode] = metricValue + rand;
  });

  return metrics;
};

export const makeStableBehavior = ({
  start, end, interval = 15000, aggregation,
}) => {
  const events = {};

  for (let i = 0; i < (end - start + interval); i += interval) {
    events[start + i] = {
      ts: start + i,
      metricValues: powerStableCalculate(aggregation),
    };
  }

  return events;
};

export const powerFallCalculate = ({
  start, end, current, aggregation,
}) => {
  let power;
  const delta = end - start;
  const metrics = {};

  METRIC_CODES.forEach((metricCode) => {
    const metricValue = getMetricValue(aggregation, metricCode);

    if (current >= end) {
      power = 0;
    } else {
      power = metricValue - (current * metricValue / delta);
    }

    metrics[metricCode] = power < 0 ? 0 : power;
  });

  return metrics;
};

export const makeFallBehavior = ({
  start, end, interval = 15000, aggregation,
}) => {
  const events = {};

  for (let i = 0; i < (end - start + interval); i += interval) {
    events[start + i] = {
      ts: start + i,
      metricValues: powerFallCalculate({
        start,
        end,
        current: i,
        aggregation,
      }),
    };
  }

  return events;
};

export const chargerSessionBehavior = ({
  start, end, interval, aggregation,
}) => {
  const delta = end - start;

  const riseStartEnd = { start, end: start + Math.floor((delta * 20 / 100)) };
  const fallStartEnd = { start: start + (delta * 80 / 100), end };
  const stableStartEnd = { start: riseStartEnd.end, end: fallStartEnd.start };

  const rise = makeRiseBehavior({ ...riseStartEnd, interval, aggregation });
  const stable = makeStableBehavior({ ...stableStartEnd, interval, aggregation });
  const fall = makeFallBehavior({ ...fallStartEnd, interval, aggregation });

  return { ...rise, ...stable, ...fall };
};

export const splitTimeFrame = (
    {
      start,
      end,
      parts = 12,
      timeout,
    },
) => {
  const frames = [];
  const delta = end - start;

  let partStart = start;
  let partEnd;

  if (parts.length) {
    parts.forEach(
        (part) => {
          const partSize = Math.floor(part * delta / 100);

          partEnd = partStart + partSize - timeout;

          frames.push({ start: partStart, end: partEnd });
          partStart += partSize;
          partEnd += partSize;
        },
    );
  } else {
    frames.push({ start, end });
  }

  return frames;
};

export const chargerSessionsRange = (
  {
    start, end, interval, aggregation, parts = 12, timeout = 0,
  },
) => {
  let sessions = {};
  const frames = splitTimeFrame({
    start, end, parts, timeout,
  });

  frames.forEach(
    (frame) => {
      sessions = {
        ...sessions,
        ...chargerSessionBehavior({
          ...frame, interval, aggregation,
        }),
      };
    },
  );

  return sessions;
};
